import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Row, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PageHeader from 'components/common/PageHeader';
import axiosInstance from 'axiosInstance';
import SalesQuickCreateModal from './components/SalesQuickCreateModal';

const SalesDashboardPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [savingOpportunityTaskId, setSavingOpportunityTaskId] = useState('');
  const [opportunities, setOpportunities] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [promoQuotas, setPromoQuotas] = useState([]);
  const [stages, setStages] = useState([]);
  const [quickCreateMode, setQuickCreateMode] = useState('');
  const [quickCreateContext, setQuickCreateContext] = useState({ companyId: '', opportunityId: '' });

  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      setLoading(true);
      setError('');
      try {
        const [
          opportunitiesResponse,
          tasksResponse,
          notificationsResponse,
          accountsResponse,
          companiesResponse,
          promoQuotasResponse,
          stagesResponse
        ] = await Promise.all([
          axiosInstance.get('sales/opportunities/'),
          axiosInstance.get('sales/activities/tasks/', { params: { mine: true } }),
          axiosInstance.get('sales/activities/notifications/', { params: { unread_only: true } }),
          axiosInstance.get('sales/customers/accounts-to-activate/'),
          axiosInstance.get('sales/companies/'),
          axiosInstance.get('sales/promo/my-quotas/'),
          axiosInstance.get('sales/pipeline/stages/')
        ]);

        if (!mounted) return;

        setOpportunities(opportunitiesResponse.data?.opportunities || []);
        setTasks(tasksResponse.data?.tasks || []);
        setNotifications(notificationsResponse.data?.notifications || []);
        setAccounts(accountsResponse.data?.accounts || []);
        setCompanies(companiesResponse.data?.companies || []);
        setPromoQuotas(promoQuotasResponse.data?.quotas || []);
        setStages(stagesResponse.data?.stages || []);
      } catch (requestError) {
        if (!mounted) return;
        const detail = requestError?.response?.data?.error || t('SALES.ERROR_LOAD_DASHBOARD');
        setError(detail);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadData();
    return () => {
      mounted = false;
    };
  }, [t]);

  const loadTasks = async () => {
    const tasksResponse = await axiosInstance.get('sales/activities/tasks/', { params: { mine: true } });
    setTasks(tasksResponse.data?.tasks || []);
  };

  const createFollowupTask = async opportunity => {
    if (!opportunity?.company_id) {
      setError(t('SALES.ERROR_CREATE_TASK'));
      return;
    }

    setSavingOpportunityTaskId(String(opportunity.id));
    setError('');
    setSuccess('');
    try {
      const dueAt = new Date(Date.now() + (24 * 60 * 60 * 1000)).toISOString();
      await axiosInstance.post('sales/activities/tasks/', {
        company_id: opportunity.company_id,
        opportunity_id: opportunity.id,
        title: `${t('SALES.FOLLOWUP_TASK_PREFIX')}: ${opportunity.title}`,
        description: t('SALES.FOLLOWUP_TASK_DESCRIPTION'),
        task_type: 'followup',
        priority: opportunity.interest_level === 'hot' ? 'high' : 'medium',
        due_at: dueAt
      });
      await loadTasks();
      setSuccess(t('SALES.TASK_CREATED_SUCCESS'));
    } catch (requestError) {
      setError(requestError?.response?.data?.error || t('SALES.ERROR_CREATE_TASK'));
    } finally {
      setSavingOpportunityTaskId('');
    }
  };

  const summary = useMemo(() => {
    const openOpportunities = opportunities.filter(item => item.status === 'open').length;
    const hotOpportunities = opportunities.filter(item => item.status === 'open' && item.interest_level === 'hot').length;
    const overdueTasks = tasks.filter(item => item.status === 'pending' && item.due_at && new Date(item.due_at) < new Date()).length;
    const dueTodayTasks = tasks.filter(item => {
      if (item.status !== 'pending' || !item.due_at) return false;
      const due = new Date(item.due_at);
      const now = new Date();
      return due.getFullYear() === now.getFullYear() && due.getMonth() === now.getMonth() && due.getDate() === now.getDate();
    }).length;

    return {
      openOpportunities,
      hotOpportunities,
      dueTodayTasks,
      overdueTasks,
      unreadAlerts: notifications.length,
      availablePromos: promoQuotas.reduce((total, item) => total + Number(item.quota_remaining || 0), 0),
      usedPromos: promoQuotas.reduce((total, item) => total + Number(item.quota_used || 0), 0),
    };
  }, [notifications.length, opportunities, promoQuotas, tasks]);

  const recentOpportunities = opportunities.slice(0, 8);
  const pendingTasks = tasks.filter(item => item.status === 'pending').slice(0, 8);
  const requiresAttention = useMemo(() => {
    const now = new Date();
    const items = [];

    opportunities.forEach(item => {
      if (item.status !== 'open') return;

      if (item.next_followup_at && new Date(item.next_followup_at) < now) {
        items.push({
          id: `followup-${item.id}`,
          title: item.title,
          body: t('SALES.ATTENTION_OVERDUE_FOLLOWUP', { company: item.company_name || '-' }),
          action: () => navigate(`/sales/opportunity/${item.id}`),
          variant: 'danger',
          relatedOpportunityId: item.id,
          relatedCompanyId: item.company_id
        });
      }

      if (item.interest_level === 'hot' && !item.last_contact_at) {
        items.push({
          id: `hot-${item.id}`,
          title: item.title,
          body: t('SALES.ATTENTION_HOT_NO_CONTACT', { company: item.company_name || '-' }),
          action: () => navigate(`/sales/opportunity/${item.id}`),
          variant: 'warning',
          relatedOpportunityId: item.id,
          relatedCompanyId: item.company_id
        });
      }

      if (!item.next_followup_at) {
        items.push({
          id: `next-step-${item.id}`,
          title: item.title,
          body: t('SALES.ATTENTION_NO_NEXT_STEP', { company: item.company_name || '-' }),
          action: () => navigate(`/sales/opportunity/${item.id}`),
          variant: 'info',
          relatedOpportunityId: item.id,
          relatedCompanyId: item.company_id
        });
      }
    });

    accounts.forEach(item => {
      if (item.onboarding_status !== 'done') {
        items.push({
          id: `account-${item.id}`,
          title: item.company_name || `#${item.company_id}`,
          body: t('SALES.ATTENTION_ACCOUNT_PENDING', { status: item.onboarding_status || item.status }),
          action: () => navigate('/sales/accounts-to-activate'),
          variant: 'secondary',
          relatedOpportunityId: item.originating_opportunity_id || '',
          relatedCompanyId: item.company_id
        });
      }
    });

    notifications.forEach(item => {
      items.push({
        id: `notification-${item.id}`,
        title: item.title,
        body: item.body,
        action: () => navigate(item.action_url || '/sales/dashboard'),
        variant: item.severity === 'urgent' ? 'danger' : item.severity === 'warning' ? 'warning' : 'info',
        notificationId: item.id
      });
    });

    return items.slice(0, 8);
  }, [accounts, navigate, notifications, opportunities, t]);

  const handleCreated = payload => {
    if (payload?.type === 'opportunity' && payload.opportunity) {
      setOpportunities(previous => [payload.opportunity, ...previous]);
    }
    if (payload?.type === 'company' && payload.company) {
      setCompanies(previous => [payload.company, ...previous]);
    }
    if (payload?.type === 'task' && payload.task) {
      setTasks(previous => [payload.task, ...previous]);
    }
  };

  const openQuickCreate = (mode, context = {}) => {
    setQuickCreateContext({
      companyId: context.companyId || '',
      opportunityId: context.opportunityId || ''
    });
    setQuickCreateMode(mode);
  };

  const postponeOpportunity = async item => {
    try {
      const postponed = new Date(Date.now() + 2 * 24 * 60 * 60 * 1000);
      const response = await axiosInstance.patch(`sales/opportunities/${item.id}/`, {
        next_followup_at: postponed.toISOString()
      });
      const updated = response.data?.opportunity;
      if (updated) {
        setOpportunities(previous => previous.map(current => (current.id === updated.id ? updated : current)));
      }
      setSuccess(t('SALES.ALERT_POSTPONED_SUCCESS'));
    } catch (requestError) {
      setError(requestError?.response?.data?.error || t('SALES.ERROR_POSTPONE_ALERT'));
    }
  };

  const dismissNotification = async notificationId => {
    try {
      await axiosInstance.post(`sales/activities/notifications/${notificationId}/dismiss/`);
      setNotifications(previous => previous.filter(item => item.id !== notificationId));
      setSuccess(t('SALES.ALERT_REVIEWED_SUCCESS'));
    } catch (requestError) {
      setError(requestError?.response?.data?.error || t('SALES.ERROR_REVIEW_ALERT'));
    }
  };

  return (
    <>
      <PageHeader title={t('SALES.DASHBOARD')} className="mb-3" />

      <Card className="mb-3 border-primary-subtle">
        <Card.Body className="d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div>
            <div className="text-primary fw-semi-bold">{t('SALES.DAILY_ACTIONS')}</div>
            <div className="text-600 fs--1">{t('SALES.DAILY_ACTIONS_HELP')}</div>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <Button size="sm" onClick={() => openQuickCreate('opportunity')}>{t('SALES.NEW_OPPORTUNITY')}</Button>
            <Button size="sm" variant="outline-primary" onClick={() => openQuickCreate('contact')}>{t('SALES.NEW_CONTACT')}</Button>
            <Button size="sm" variant="outline-primary" onClick={() => openQuickCreate('task')}>{t('SALES.NEW_TASK')}</Button>
            <Button size="sm" variant="outline-dark" onClick={() => openQuickCreate('company')}>{t('SALES.NEW_COMPANY')}</Button>
          </div>
        </Card.Body>
      </Card>

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {success ? <Alert variant="success">{success}</Alert> : null}

      {loading ? (
        <Card className="p-4 text-center">
          <Spinner animation="border" />
        </Card>
      ) : (
        <>
          <Row className="g-3 mb-3">
            <Col md={6} xl={3}>
              <Card className="h-100">
                <Card.Body>
                  <div className="text-600 fs--1">{t('SALES.OPEN_OPPORTUNITIES')}</div>
                  <div className="fs-2 fw-bold">{summary.openOpportunities}</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} xl={3}>
              <Card className="h-100">
                <Card.Body>
                  <div className="text-600 fs--1">{t('SALES.HOT_OPPORTUNITIES')}</div>
                  <div className="fs-2 fw-bold text-warning">{summary.hotOpportunities}</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} xl={3}>
              <Card className="h-100">
                <Card.Body>
                  <div className="text-600 fs--1">{t('SALES.TASKS_DUE_TODAY')}</div>
                  <div className="fs-2 fw-bold text-primary">{summary.dueTodayTasks}</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} xl={3}>
              <Card className="h-100">
                <Card.Body>
                  <div className="text-600 fs--1">{t('SALES.OVERDUE_TASKS')}</div>
                  <div className="fs-2 fw-bold text-danger">{summary.overdueTasks}</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} xl={3}>
              <Card className="h-100">
                <Card.Body>
                  <div className="text-600 fs--1">{t('SALES.AVAILABLE_PROMOS')}</div>
                  <div className="fs-2 fw-bold text-primary">{summary.availablePromos}</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} xl={3}>
              <Card className="h-100">
                <Card.Body>
                  <div className="text-600 fs--1">{t('SALES.USED_THIS_MONTH')}</div>
                  <div className="fs-2 fw-bold text-success">{summary.usedPromos}</div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-3">
            <Col xl={7}>
              <Card className="h-100">
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <span className="fw-semi-bold">{t('SALES.RECENT_OPPORTUNITIES')}</span>
                  <Badge bg="soft-info" text="info">{recentOpportunities.length}</Badge>
                </Card.Header>
                <Card.Body className="pt-2">
                  {recentOpportunities.length ? recentOpportunities.map(item => (
                    <div key={item.id} className="py-2 border-bottom">
                      <div className="d-flex justify-content-between align-items-start gap-2">
                        <div>
                          <div className="fw-semi-bold">{item.title}</div>
                          <div className="d-flex gap-2 flex-wrap mt-1">
                            {item.promo ? <Badge bg="soft-warning" text="warning">{t('SALES.HAS_PROMO_BADGE')}</Badge> : null}
                            {item.promo?.expires_soon ? <Badge bg="soft-danger" text="danger">{t('SALES.PROMO_EXPIRING_SOON')}</Badge> : null}
                          </div>
                          <div className="text-600 fs--1">{item.pipeline_stage_name || t('SALES.NO_STAGE')}</div>
                        </div>
                        <Badge bg={item.interest_level === 'hot' ? 'danger' : item.interest_level === 'warm' ? 'warning' : 'secondary'}>
                          {item.interest_level || 'cold'}
                        </Badge>
                      </div>
                      <div className="mt-2">
                        <div className="d-flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            disabled={savingOpportunityTaskId === String(item.id)}
                            onClick={() => createFollowupTask(item)}
                          >
                            {savingOpportunityTaskId === String(item.id)
                              ? t('SALES.SAVING')
                              : t('SALES.CREATE_FOLLOWUP_TASK')}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => navigate(`/sales/opportunity/${item.id}`)}
                          >
                            {t('SALES.OPEN_DEAL')}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-dark"
                            onClick={() => navigate(`/sales/company/${item.company_id}`)}
                          >
                            {t('SALES.VIEW_COMPANY')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-4">
                      <div className="text-600 mb-3">{t('SALES.EMPTY_OPPORTUNITIES_DASHBOARD')}</div>
                      <Button size="sm" onClick={() => openQuickCreate('opportunity')}>
                        {t('SALES.CREATE_FIRST_OPPORTUNITY')}
                      </Button>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col xl={5}>
              <Card className="h-100">
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <span className="fw-semi-bold">{t('SALES.PENDING_TASKS')}</span>
                  <Badge bg="soft-primary" text="primary">{pendingTasks.length}</Badge>
                </Card.Header>
                <Card.Body className="pt-2">
                  {pendingTasks.length ? pendingTasks.map(item => (
                    <div key={item.id} className="py-2 border-bottom d-flex justify-content-between align-items-center gap-2">
                      <div>
                        <div className="fw-semi-bold">{item.title}</div>
                        <div className="text-600 fs--1">{item.due_at ? new Date(item.due_at).toLocaleString() : '-'}</div>
                      </div>
                      <Badge bg={item.priority === 'urgent' ? 'danger' : item.priority === 'high' ? 'warning' : 'secondary'}>
                        {item.priority}
                      </Badge>
                    </div>
                  )) : (
                    <div className="text-center py-4">
                      <div className="text-600 mb-3">{t('SALES.EMPTY_TASKS_DASHBOARD')}</div>
                      <Button size="sm" variant="outline-primary" onClick={() => openQuickCreate('task')}>
                        {t('SALES.NEW_TASK')}
                      </Button>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-3 mt-1">
            <Col xl={8}>
              <Card className="h-100">
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <span className="fw-semi-bold">{t('SALES.REQUIRES_ATTENTION')}</span>
                  <Badge bg="soft-danger" text="danger">{requiresAttention.length}</Badge>
                </Card.Header>
                <Card.Body className="pt-2">
                  {requiresAttention.length ? requiresAttention.map(item => (
                    <div key={item.id} className="py-2 border-bottom d-flex justify-content-between align-items-start gap-3 flex-wrap">
                      <div>
                        <div className="fw-semi-bold">
                          <Badge bg={`soft-${item.variant}`} text={item.variant} className="me-2">
                            {item.variant}
                          </Badge>
                          {item.title}
                        </div>
                        <div className="text-600 fs--1">{item.body}</div>
                      </div>
                      <div className="d-flex gap-2 flex-wrap">
                        <Button size="sm" variant={`outline-${item.variant}`} onClick={item.action}>
                          {t('SALES.VIEW')}
                        </Button>
                        {item.relatedOpportunityId ? (
                          <>
                            <Button size="sm" variant="outline-primary" onClick={() => openQuickCreate('task', { companyId: item.relatedCompanyId, opportunityId: item.relatedOpportunityId })}>
                              {t('SALES.NEW_TASK')}
                            </Button>
                            <Button size="sm" variant="outline-secondary" onClick={() => navigate(`/sales/opportunity/${item.relatedOpportunityId}`)}>
                              {t('SALES.LOG_ACTIVITY')}
                            </Button>
                            <Button size="sm" variant="outline-dark" onClick={() => postponeOpportunity({ id: item.relatedOpportunityId })}>
                              {t('SALES.POSTPONE')}
                            </Button>
                          </>
                        ) : null}
                        {item.notificationId ? (
                          <Button size="sm" variant="outline-dark" onClick={() => dismissNotification(item.notificationId)}>
                            {t('SALES.MARK_REVIEWED')}
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-4 text-600">{t('SALES.EMPTY_ATTENTION')}</div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col xl={4}>
              <Card className="h-100">
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <span className="fw-semi-bold">{t('SALES.ACCOUNTS_TO_ACTIVATE')}</span>
                  <Badge bg="soft-info" text="info">{accounts.length}</Badge>
                </Card.Header>
                <Card.Body className="pt-2">
                  {accounts.length ? accounts.slice(0, 5).map(item => (
                    <div key={item.id} className="py-2 border-bottom">
                      <div className="fw-semi-bold">{item.company_name || item.company_id}</div>
                      <div className="text-600 fs--1">{item.billing_email || '-'}</div>
                      <div className="mt-2 d-flex justify-content-between align-items-center">
                        <Badge bg={item.status === 'active' ? 'success' : 'warning'}>{item.onboarding_status || item.status}</Badge>
                        <Button size="sm" variant="outline-secondary" onClick={() => navigate('/sales/accounts-to-activate')}>
                          {t('SALES.OPEN')}
                        </Button>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-4">
                      <div className="text-600 mb-3">{t('SALES.EMPTY_ACCOUNTS_DASHBOARD')}</div>
                      <Button size="sm" variant="outline-secondary" onClick={() => navigate('/sales/pipeline')}>
                        {t('SALES.VIEW_WON_OPPORTUNITIES')}
                      </Button>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}

      <SalesQuickCreateModal
        show={Boolean(quickCreateMode)}
        mode={quickCreateMode || 'opportunity'}
        onHide={() => {
          setQuickCreateMode('');
          setQuickCreateContext({ companyId: '', opportunityId: '' });
        }}
        companies={companies}
        opportunities={opportunities}
        stages={stages}
        defaultCompanyId={quickCreateContext.companyId}
        defaultOpportunityId={quickCreateContext.opportunityId}
        onCreated={handleCreated}
        onRequestModeChange={setQuickCreateMode}
      />
    </>
  );
};

export default SalesDashboardPage;
